import RefreshIcon from "@mui/icons-material/Refresh";
import { forwardRef, useImperativeHandle, useRef } from "react";
import { useFormContext } from "react-hook-form";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonTertiary,
  MuiGrid,
  MuiSelect,
} from "../../../../components";
import { ClientAutoComplete } from "../../../../components/clientAutoComplete/ClientAutoComplete";
import { CURRENCY_FORMAT_MAP } from "../../../../components/textFieldCurrency/CurrencyUtils";
import { TextFieldCurrency } from "../../../../components/textFieldCurrency/TextFieldCurrency";
import { gridSize } from "../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { searchPendingBillingFormKeys } from "../../modelsAndSchemas/models";
import { PendingBillingSearchFormWrapper } from "../../ReportSearchStyle";
import { reportsSearchReportsSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

export interface PendingBillingSearchFormRef {
  reset: () => void;
}
export interface PendingBillingListProps {
  onResetSearch: () => void;
  onSubmit: () => void;
  defaultOutStandingAmount: string;
}
export const PendingBillingSearchForm = forwardRef<
  PendingBillingSearchFormRef,
  PendingBillingListProps
>(({ onResetSearch, onSubmit, defaultOutStandingAmount }, ref) => {
  const {
    fetchComponentTitle,
    commonLabels,
    useFetchLabel,
    fetchDropdownValues,
    fetchValidationMessage,
  } = useSiteCore();
  const { isMobile } = useMediaQueries();

  const { reset, control, setValue, clearErrors } = useFormContext();

  useImperativeHandle(ref, () => ({
    reset: handleClearFilter,
  }));

  const handleClearFilter = () => {
    clearErrors();
    reset();
    clientRef.current?.resetValues();
    onResetSearch();
  };

  const getLabel = useFetchLabel(reportsSearchReportsSiteCoreKeys.header);
  const { commonARIALabel } = useNavigationSiteCore();
  const clientRef = useRef<{ resetValues: (values?: string[]) => void }>(null);

  const renderPendingBillHeader = () => {
    return (
      <MuiGrid container className="form-header">
        <MuiGrid justifySelf="end" className="form-header-right">
          <MuiButtonTertiary onClick={handleClearFilter}>
            <RefreshIcon /> {commonLabels.resetAllSearch}
          </MuiButtonTertiary>
        </MuiGrid>
      </MuiGrid>
    );
  };

  return (
    <PendingBillingSearchFormWrapper>
      <AccordionHeader
        showExpandCollapseIcon={true}
        title={
          <AccordionHeaderText
            label={fetchComponentTitle(reportsSearchReportsSiteCoreKeys.header)}
          />
        }
      >
        {renderPendingBillHeader()}
        <MuiBox className="selectReportFieldContainer">
          <MuiGrid container columnSpacing={2} rowSpacing={isMobile ? 2 : 4}>
            <MuiGrid size={gridSize}>
              <FormController
                control={control}
                name={searchPendingBillingFormKeys.officeType}
              >
                <MuiSelect
                  visibleDefaultSelectOption
                  id={searchPendingBillingFormKeys.officeType}
                  isMandatory
                  label={getLabel(
                    reportsSearchReportsSiteCoreKeys.officeTypeDropDown,
                  )}
                  options={fetchDropdownValues(
                    reportsSearchReportsSiteCoreKeys.header,
                    reportsSearchReportsSiteCoreKeys.officeTypeList,
                  )}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize}>
              <FormController
                control={control}
                name={searchPendingBillingFormKeys.includeClosed}
              >
                <MuiSelect
                  visibleDefaultSelectOption
                  id={searchPendingBillingFormKeys.includeClosed}
                  label={getLabel(
                    reportsSearchReportsSiteCoreKeys.includeClosedDropDown,
                  )}
                  options={fetchDropdownValues(
                    reportsSearchReportsSiteCoreKeys.header,
                    reportsSearchReportsSiteCoreKeys.includeClosedList,
                  )}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize}>
              <FormController
                control={control}
                name={searchPendingBillingFormKeys.clientIds}
                ref={clientRef}
              >
                <ClientAutoComplete
                  label={getLabel(
                    reportsSearchReportsSiteCoreKeys.clientNameDropDown,
                  )}
                  formId={searchPendingBillingFormKeys.clientIds}
                  control={control}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize}>
              <FormController
                control={control}
                name={searchPendingBillingFormKeys.outStandingAmount}
              >
                <TextFieldCurrency
                  label={getLabel(
                    reportsSearchReportsSiteCoreKeys.outstandingAmountDropDown,
                  )}
                  formId={searchPendingBillingFormKeys.outStandingAmount}
                  locale={CURRENCY_FORMAT_MAP.USD.locale}
                  formatErrorMessage={fetchValidationMessage(
                    commonSiteCoreKeys.alphabetNotAllowedValidator,
                  )}
                  name={searchPendingBillingFormKeys.outStandingAmount}
                  zeroValidationMessage={fetchValidationMessage(
                    commonSiteCoreKeys.greaterThanZeroFieldValidator,
                  )}
                  getFinalValues={(valueWithFormat, value) => {
                    setValue(
                      searchPendingBillingFormKeys.outStandingAmountWithFormat,
                      valueWithFormat,
                    );
                    setValue(
                      searchPendingBillingFormKeys.outStandingAmount,
                      Number(value),
                    );
                  }}
                  requiredARIALabel={commonARIALabel.required}
                  defaultValue={defaultOutStandingAmount}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
        <MuiBox className="selectReportButtonContainer" textAlign="right">
          <MuiButtonPrimary onClick={onSubmit}>
            {commonLabels.search}
          </MuiButtonPrimary>
        </MuiBox>
      </AccordionHeader>
    </PendingBillingSearchFormWrapper>
  );
});
